import { useEffect } from "react";
import Hero from "@/components/clinic/Hero";
import Approach from "@/components/clinic/Approach";
import Gallery from "@/components/clinic/Gallery";
import ResultsGallery from "@/components/clinic/ResultsGallery";
import Offer from "@/components/clinic/Offer";
import FAQ from "@/components/clinic/FAQ";
import Booking from "@/components/clinic/Booking";
import LocationSEO from "@/components/clinic/LocationSEO";
import Footer from "@/components/clinic/Footer";
import { applySeo } from "@/lib/seo";
import { startHeightSync } from "@/lib/embed";

/**
 * Iframe-friendly homepage rendered at /embed.
 *
 * Differences vs the public homepage:
 *  - No top Navbar (the host site already has its own navigation).
 *  - Posts its height to the parent window so Wix can auto-grow the iframe.
 *  - `noindex` so the duplicate URL doesn't compete with the canonical home.
 *  - Hard-coded `width: 100%` and overflow rules so it sits flush in any host.
 */
const Embed = () => {
  useEffect(() => {
    applySeo({
      title: "Dr Vahe Clinic — Embedded",
      description:
        "Embedded view of the Dr Vahe Clinic homepage for use inside partner sites.",
      path: "/embed",
    });

    // Prevent search engines from indexing the embed URL.
    let robots = document.querySelector(
      'meta[name="robots"]',
    ) as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    const previous = robots.getAttribute("content");
    robots.setAttribute("content", "noindex, follow");

    // Keep parent iframe height in sync with our content.
    const stop = startHeightSync();

    // Tag <html> so embed-only CSS overrides can hook in.
    document.documentElement.dataset.embed = "1";

    return () => {
      stop();
      delete document.documentElement.dataset.embed;
      if (previous != null) robots?.setAttribute("content", previous);
      else robots?.setAttribute("content", "index, follow");
    };
  }, []);

  return (
    <div className="min-h-0 w-full bg-background overflow-x-hidden">
      {/* Skip link — invisible until focused. Lets keyboard users (and the
          host site's screen reader pass-through) jump straight to the CTA
          without traversing every gallery section. */}
      <a
        href="#booking"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[70] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-full focus:text-[10px] focus:uppercase focus:tracking-[0.3em]"
      >
        Skip to booking
      </a>
      <main>
        <Hero />
        <Approach />
        <Gallery />
        <ResultsGallery />
        <Offer />
        <FAQ />
        <Booking />
        <LocationSEO />
      </main>
      <Footer />
      {/* Mobile-only sticky CTA. Without this, an iframe-embedded visitor on
          a phone has to scroll the host page past every section to reach the
          booking form. We hide on md+ where the desktop layout already keeps
          the CTA in view. */}
      <a
        href="#booking"
        className="md:hidden fixed bottom-3 left-3 right-3 z-[55] py-4 bg-primary text-primary-foreground rounded-full text-[10px] uppercase tracking-[0.3em] text-center shadow-elegant active:bg-accent transition-colors"
      >
        Enquire now
      </a>
    </div>
  );
};

export default Embed;
